<template>
  <div>
    <div class="i-toolbar flex justify-between">
      <div class="inline-block">
        <h1 class="inline-block ph2">{{ title }}</h1>
        <TreeModeDropdown v-if="isTreeModeSwitchEnabled"/>
      </div>

      <div class="inline-block">
        <button
          @click.prevent="() => toggleFiltersToolbar()"
          class="i-button btn i-simple-btn">
          <i class="fas fa5" :class="isFiltersToolbarOpen ? 'fa-minus-circle' : 'fa-filter'" aria-hidden="true"></i>
          <span>{{ isFiltersToolbarOpen ? LANG.clearFilter : LANG.setFilter }}</span>
        </button>

        <ToolbarButtonColumns />
        <ToolbarButtonSavedFilters />

        <i-button v-if="false" @click.prevent="() => toggleBrowserMode()">
          <i-icon :icon="browserModeToggleIcon" size="small"></i-icon>
        </i-button>

        <FilterFreeSearchInput :placeholder="simpleSearchPlaceholder" />
      </div>
    </div>

    <div id="i-panelview-container">
      <PanelLeft>
        <TreeList :nodes="treeNodes"/>
        <template v-slot:footer>
          <label class="i-material-checkbox i-material-small">
            <input
              type="checkbox"
              :checked="treeRecursiveModeGetter"
              @input="() => setTreeRecursiveMode(!treeRecursiveModeGetter)"
              :disabled="isTreeRecursiveModeDisabled" />
            <span>{{ includeSubNodes }}</span>
          </label>
        </template>
      </PanelLeft>

      <PanelCenter>
        <FiltersToolbar />
        <EntityTable
          @documentAction="handleDocumentAction"
        />
        <template v-slot:footer>
          <PanelCenterFooterDocumentCount />
          <PanelCenterFooterMassActions v-if="!isInsertMode" />
        </template>
      </PanelCenter>

      <PanelRight>
        <DocumentInspector
          :document="inspectedDocument"
          @documentAction="handleDocumentAction"
        />
      </PanelRight>
    </div>

    <modal-create-category />

    <ModalInformation :document="this.modalDocument" v-model:show="modals.info" />
    <ModalFocusPoint :document="this.modalDocument" v-model:show="modals.focusPoint"/>
    <ModalHistory :document="this.modalDocument" v-model:show="modals.history"/>
    <ModalDifferences :document="this.modalDocument" v-model:show="modals.differences"/>
    <ModalVariants :document="this.modalDocument" :insertMode="isInsertMode" v-model:show="modals.variants"/>
    <ModalArchiveVersions :document="this.modalDocument" v-model:show="modals.archive"/>
    <ModalLiveUri :document="this.modalDocument" v-model:show="modals.liveUri"/>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapState, mapMutations } from 'vuex';
import { mapBrowserState, mapBrowserActions, mapBrowserMutations, mapBrowserGetters } from '@/store'; // eslint-disable-line
import { BrowserMode, TreeMode } from '@/store/modules/entity_browser/config';
import TreeList from '@/components/browser/tree/TreeList.vue';
import ToolbarButtonColumns from '@/components/browser/ToolbarButtonColumns.vue';
import ToolbarButtonSavedFilters from '@/components/browser/ToolbarButtonSavedFilters.vue';
import TreeModeDropdown from '@/components/browser/tree/TreeModeDropdown.vue';
import FilterFreeSearchInput from '@/components/browser/filters/FilterFreeSearchInput.vue';
import PanelCenter from '@/components/common/PanelCenter.vue';
import PanelLeft from '@/components/common/PanelLeft.vue';
import PanelRight from '@/components/common/PanelRight.vue';
import DocumentInspector from '@/components/browser/documents/DocumentInspector.vue';
import EntityTable from '@/components/browser/documents/EntityTable.vue';
import FiltersToolbar from '@/components/browser/filters/FiltersToolbar.vue';
import PanelCenterFooterMassActions from '@/components/browser/panel_center/PanelCenterFooterMassActions.vue';
import PanelCenterFooterDocumentCount from '@/components/browser/panel_center//PanelCenterFooterDocumentCount.vue';
import ModalInformation from '@/components/browser/documents/ModalInformation.vue';
import ModalHistory from '@/components/browser/documents/ModalHistory.vue';
import ModalFocusPoint from '@/components/browser/documents/ModalFocusPoint.vue';
import ModalDifferences from '@/components/browser/documents/ModalDifferences.vue';
import ModalArchiveVersions from '@/components/browser/documents/ModalArchiveVersions.vue';
import ModalVariants from '@/components/browser/documents/ModalVariants.vue';
import ModalLiveUri from '@/components/browser/documents/ModalLiveUri.vue';
import ModalCreateCategory from './ModalCreateCategory.vue';

export default defineComponent({
  components: {
    ToolbarButtonColumns,
    ToolbarButtonSavedFilters,
    FilterFreeSearchInput,
    PanelLeft,
    TreeList,
    PanelRight,
    DocumentInspector,
    PanelCenter,
    PanelCenterFooterMassActions,
    TreeModeDropdown,
    EntityTable,
    FiltersToolbar,
    ModalCreateCategory,
    PanelCenterFooterDocumentCount,
    ModalInformation,
    ModalFocusPoint,
    ModalHistory,
    ModalDifferences,
    ModalVariants,
    ModalArchiveVersions,
    ModalLiveUri,
  },
  props: {
    mode: {
      type: String,
      default: BrowserMode.DOC,
    },
  },
  data: () => ({
    modals: {
      info: false,
      focusPoint: false,
      history: false,
      differences: false,
      variants: false,
      archive: false,
      liveUri: false,
    },
    modalDocument: {},
  }),
  created() {
    this.setBrowserMode(this.mode);
    this.checkInsertMode();

    // TODO: this should be moved to `beforeRouteEnter`
    // so that the filterQuery is reset on navigation change
    // not only when the component is mounted/created
    // however, the static router currently does not mimic the
    // `beforeRouteEnter` API of vue-router
    this.restoreFilterQueryFromRoute();
    this.restoreCatQueryFromRoute();
  },
  watch: {
    mode(m) {
      // TODO: it would be probably better to
      // pass the browserMode prop to the components
      // via prop drilling
      // or use inject/provide as an alternative
      // instead of keeping it in the store
      // and using prop watchers to sync
      // the store state with the prop
      this.setBrowserMode(m);
    },
  },
  mounted() {
    this.$store.dispatch('ui/initImperiaLayout', true);
    this.fetchModuleData();
  },
  computed: {
    ...mapState(['LANG', 'browserMode']),
    ...mapBrowserState([
      'isFiltersToolbarOpen',
      'treeMode',
      'isInsertMode',
      'inspectedDocument',
    ]),
    ...mapBrowserGetters([
      'treeNodes',
      'isTreeRecursiveModeDisabled',
      'treeRecursiveModeGetter',
    ]),
    title() {
      return {
        [BrowserMode.DOC]: this.LANG.documentBrowser,
        [BrowserMode.MAM]: this.LANG.mediaManagement,
      }[this.browserMode];
    },
    browserModeToggleIcon() {
      return {
        [BrowserMode.DOC]: 'sitemap',
        [BrowserMode.MAM]: 'images',
      }[this.browserMode];
    },
    isTreeModeSwitchEnabled() {
      return {
        [BrowserMode.DOC]: true,
        [BrowserMode.MAM]: false,
      }[this.browserMode];
    },
    simpleSearchPlaceholder() {
      return {
        [BrowserMode.DOC]: this.LANG.searchInDocuments,
        [BrowserMode.MAM]: this.LANG.searchInMedia,
      }[this.browserMode];
    },
    includeSubNodes() {
      return {
        [TreeMode.CATEGORY]: this.LANG.includingSubcategories,
        [TreeMode.DIRECTORY]: this.LANG.includingSubdirectories,
      }[this.treeMode];
    },
  },
  methods: {
    ...mapBrowserActions([
      'fetchModuleData',
      'checkInsertMode',
      'restoreFilterQueryFromRoute',
      'restoreCatQueryFromRoute',
    ]),
    ...mapMutations([
      'toggleBrowserMode',
      'setBrowserMode',
    ]),
    ...mapBrowserMutations([
      'toggleFiltersToolbar',
      'setTreeRecursiveMode',
    ]),
    handleDocumentAction({ action, document } = {}) {
      if (!Object.keys(this.modals).includes(action)) {
        console.error('Unsupported document action', action);
        return;
      }

      this.modals[action] = true;
      this.modalDocument = document;
    },
  },
});
</script>
